import { useMutation, useQueryClient } from '@tanstack/react-query'
import { BookOpen, ChefHat, ChevronDown, Pencil, Printer, Star } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { MEAL_NAMES } from '../../shared/meals'
import { NewBook } from '@/components/BooksSection'
import { ConfirmButton } from '@/components/ConfirmButton'
import { SendRecipes } from '@/components/SendTo'
import { FormError } from '@/components/FormError'
import { OriginalPhoto } from '@/components/OriginalPhoto'
import { page } from '@/components/layout'
import { Initial } from '@/components/paper'
import { MadeIt } from '@/components/recipes/MadeIt'
import { MadeByTheFamily, RecipePost } from '@/components/recipes/RecipePost'
import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'
import { useFamily } from '@/lib/families'
import { useItemHome, usePageFrame } from '@/lib/frame'
import { photoUrl } from '@/lib/photos'
import { cardPhotos, dishPhotos, lines, madePhotos, NO_BOOK, useRecipe, type Recipe } from '@/lib/recipes'
import { usePendingUploads } from '@/lib/uploadQueue'
import { useMe } from '@/lib/useMe'
import { cn } from '@/lib/utils'
import { PageLoader } from '@/components/Loader'

export function RecipePage() {
  const { id = '' } = useParams()
  const recipe = useRecipe(id)
  if (recipe.isPending) return <PageLoader />
  if (recipe.isError) return <FormError error={recipe.error} />
  return <RecipeView recipe={recipe.data} />
}

// Where a recipe lives, to go back to: its book, or the recipes in no book.
const whereFrom = (recipe: Recipe, home: string) =>
  recipe.book
    ? { to: `/book/${recipe.book.id}`, title: recipe.book.title }
    : { to: `/circle/${home}/recipes?loose=1`, title: NO_BOOK }

function RecipeView({ recipe }: { recipe: Recipe }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const home = useItemHome(recipe)
  const where = whereFrom(recipe, home)
  const family = useFamily(recipe.familyId)
  const [sending, setSending] = useState(false)
  const [starting, setStarting] = useState(false)
  const remove = useMutation({
    mutationFn: () => api(`/recipes/${recipe.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['book'] })
      await queryClient.invalidateQueries({ queryKey: ['books', recipe.familyId] })
      navigate(where.to, { replace: true })
    },
  })
  const ingredients = lines(recipe.ingredients)
  const steps = lines(recipe.steps)
  const waiting = usePendingUploads(recipe.id)
  const [shown, setShown] = useState(0)
  const cards = cardPhotos(recipe)
  const dishes = dishPhotos(recipe)
  const photo = cards[Math.min(shown, cards.length - 1)]
  // The keeper picks the photo for the recipe's tile right here, without opening the whole form. The edit sends the
  // words as they are, since a PATCH without them would empty them.
  const tile = useMutation({
    mutationFn: (photoId: string) =>
      api(`/recipes/${recipe.id}`, {
        method: 'PATCH',
        body: {
          title: recipe.title,
          ingredients: recipe.ingredients,
          steps: recipe.steps,
          notes: recipe.notes,
          heroPhotoId: photoId,
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['recipe', recipe.id] })
      await queryClient.invalidateQueries({ queryKey: ['book'] })
      await queryClient.invalidateQueries({ queryKey: ['books', recipe.familyId] })
    },
  })
  const pickTile = recipe.mine ? (photoId: string) => tile.mutate(photoId) : undefined
  const modern = useMe().data?.recipesStyle === 'modern'
  usePageFrame(home, {
    title: recipe.title,
    back: { to: where.to, label: `Back to ${where.title}` },
  })

  const keeperSheets = recipe.mine && (
    <>
      <SendRecipes
        open={sending && !starting}
        onOpenChange={setSending}
        familyId={recipe.familyId}
        recipes={[recipe.id]}
        from={recipe.book?.id}
        inBook={!!recipe.book}
        onSent={() => setSending(false)}
        onNewBook={() => setStarting(true)}
      />
      {starting && family.data && (
        <NewBook
          open
          onOpenChange={(open) => !open && setStarting(false)}
          family={family.data}
          space={null}
          first={false}
          send={[recipe.id]}
        />
      )}
    </>
  )
  const deleting = recipe.mine && (
    <section className="flex flex-col gap-2 pt-2">
      <FormError error={remove.error} />
      <ConfirmButton
        label="Delete this recipe"
        title={`Delete ${recipe.title}?`}
        description="It will be removed for everyone it’s shared with."
        confirm="Delete recipe"
        disabled={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
    </section>
  )
  const waitingNote = waiting > 0 && (
    <p role="status" className="rounded-2xl border border-dashed p-3 text-sm text-muted-foreground">
      {waiting === 1 ? '1 photo is' : `${waiting} photos are`} saved on this phone and will upload when you have signal.
    </p>
  )

  if (modern)
    return (
      <>
        <RecipePost
          recipe={recipe}
          waiting={waitingNote}
          keeper={
            recipe.mine && (
              <button
                type="button"
                onClick={() => setSending(true)}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-secondary px-5 text-sm font-medium transition-colors outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                <BookOpen className="size-4" aria-hidden /> Send to book
              </button>
            )
          }
        />
        {keeperSheets}
        {deleting && <div className="mx-auto w-full max-w-6xl px-4 pb-10 lg:px-6">{deleting}</div>}
      </>
    )

  return (
    <article className={cn(page, 'flex flex-col gap-8 pt-4 nav:pt-12 lg:flex-row lg:items-start lg:gap-14')}>
      {photo && (
        // The original card on the kitchen table: large, straight and untouched. With more than one photo, the
        // others wait to be picked: in a column beside it on a tablet or computer, which stays put while the photo
        // changes, and in a row under it on a phone. The photo's place keeps one height there too, so nothing
        // around it jumps from a tall card to a wide one. That height follows the room beside the column, enough for an
        // upright card to fill it, so a narrow page isn't left with a tall empty space.
        <figure className="@container flex min-w-0 flex-col gap-5 rounded-[2rem] table-surface p-4 [--stage:min(70svh,44rem,calc((100cqw-5.5rem)*1.35))] sm:p-6 lg:sticky lg:top-8 lg:flex-[3_1_0%] lg:p-8">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
            {cards.length > 1 && (
              <div
                role="group"
                aria-label="Photos of the card"
                className="order-last flex gap-3.5 overflow-x-auto p-1 sm:order-first sm:max-h-(--stage) sm:shrink-0 sm:flex-col sm:overflow-x-visible sm:overflow-y-auto"
              >
                {cards.map((p, i) => (
                  <button
                    key={p.id}
                    type="button"
                    aria-label={`Show photo ${i + 1} of ${cards.length}`}
                    aria-pressed={p.id === photo.id}
                    onClick={() => setShown(i)}
                    className="rounded-paper relative size-16 shrink-0 bg-paper p-1 shadow-paper transition-[translate] duration-300 ease-settle outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-pressed:-translate-y-1 aria-pressed:ring-2 aria-pressed:ring-brand sm:aria-pressed:translate-x-1 sm:aria-pressed:translate-y-0 lg:size-18"
                  >
                    <img
                      src={photoUrl(p.ref, 'thumb')}
                      alt=""
                      className="size-full rounded-[2px] bg-secondary object-cover"
                    />
                    {p.ref === recipe.cover && <TileMark />}
                  </button>
                ))}
              </div>
            )}
            <div className="flex min-w-0 flex-1 items-center justify-center sm:h-(--stage)">
              <OriginalPhoto
                src={photoUrl(photo.id, 'original')}
                fallback={photoUrl(photo.ref, 'display')}
                alt={`The original card for ${recipe.title}`}
              >
                <button
                  type="button"
                  className="rounded-paper block w-fit max-w-full bg-paper p-1.5 shadow-paper outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 sm:p-2"
                >
                  <img
                    src={photoUrl(photo.ref, 'display')}
                    width={photo.width}
                    height={photo.height}
                    alt={`The card for ${recipe.title}, photo ${cards.indexOf(photo) + 1} of ${cards.length}`}
                    className="block h-auto max-h-[70svh] w-auto max-w-full rounded-[2px] object-contain sm:max-h-[calc(var(--stage)-1rem)]"
                  />
                </button>
              </OriginalPhoto>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
            {pickTile && (
              <TileChoice onTile={photo.ref === recipe.cover} busy={tile.isPending} pick={() => pickTile(photo.id)} />
            )}
            <figcaption className="ml-auto whisper">Kept exactly as photographed.</figcaption>
          </div>
          <FormError error={tile.error} />
        </figure>
      )}

      <div
        className={cn('flex min-w-0 flex-col gap-6', photo ? 'lg:flex-[2_1_0%]' : 'mx-auto w-full max-w-(--measure)')}
      >
        <header className="flex flex-col gap-2.5">
          <Link to={where.to} className="inline-flex min-h-11 items-center self-start eyebrow hover:underline">
            {where.title}
          </Link>
          <h1 className="text-display">{recipe.title}</h1>
          {recipe.from && (
            <p className="text-lg font-semibold tracking-tight">
              From{' '}
              <Link to={`/person/${recipe.from.id}`} className="underline-offset-4 hover:underline">
                {recipe.from.name}
              </Link>
            </p>
          )}
          <p className="flex items-center gap-2.5">
            <Initial name={recipe.by} portrait={recipe.byPortrait} index={2} className="size-7 text-xs" />
            Added by {recipe.by}
          </p>
          {recipe.meals.length > 0 && (
            <ul aria-label="What it’s for" className="flex flex-wrap gap-2">
              {recipe.meals.map((meal) => (
                <li key={meal}>
                  <Link
                    to={
                      recipe.book
                        ? `/book/${recipe.book.id}?meal=${meal}`
                        : `/circle/${recipe.familyId}/recipes?view=all&by=meal`
                    }
                    className="inline-flex min-h-9 items-center rounded-full bg-secondary px-3.5 text-sm font-medium transition-colors outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 pointer-coarse:min-h-11"
                  >
                    {MEAL_NAMES[meal].one}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </header>

        {waitingNote}

        {/* Cooking, printing and editing sit side by side in the page; nothing floats over the recipe. */}
        <div className="flex flex-wrap gap-2.5 max-nav:*:flex-1 nav:flex-col">
          <Button asChild size="lg" className="max-nav:basis-full">
            <Link to={`/recipe/${recipe.id}/cook`}>
              <ChefHat aria-hidden /> Start cooking
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <Link to={`/recipe/${recipe.id}/print`}>
              <Printer aria-hidden /> Print
            </Link>
          </Button>
          {recipe.mine && (
            <Button asChild size="lg" variant="outline">
              <Link to={`/recipe/${recipe.id}/edit`}>
                <Pencil aria-hidden /> Edit recipe
              </Link>
            </Button>
          )}
          {recipe.mine && (
            <Button size="lg" variant="outline" onClick={() => setSending(true)}>
              <BookOpen aria-hidden /> Send to book
            </Button>
          )}
        </div>
        {keeperSheets}

        {(ingredients.length > 0 || steps.length > 0 || recipe.notes.trim()) && (
          <div className="border-t">
            {ingredients.length > 0 && (
              <Fold title="Ingredients" open>
                <ul className="list-disc space-y-1.5 pl-5">
                  {ingredients.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              </Fold>
            )}
            {steps.length > 0 && (
              <Fold title="Steps">
                <ol className="list-decimal space-y-2.5 pl-5">
                  {steps.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ol>
              </Fold>
            )}
            {recipe.notes.trim() && (
              <Fold title="Notes from the circle">
                <p className="whitespace-pre-wrap">{recipe.notes.trim()}</p>
              </Fold>
            )}
          </div>
        )}

        {dishes.length > 0 && (
          <DishGallery
            title={recipe.title}
            photos={dishes}
            cover={recipe.cover}
            pick={pickTile}
            busy={tile.isPending}
          />
        )}

        <div className="flex flex-col gap-3">
          <MadeByTheFamily recipe={recipe} photos={madePhotos(recipe)} />
          <MadeIt recipeId={recipe.id} className="self-start border" />
        </div>

        {deleting}
      </div>
    </article>
  )
}

// Photos of the dish, made and served: a small gallery beside the card. Each opens as the original.
function DishGallery({
  title,
  photos,
  cover,
  pick,
  busy,
}: {
  title: string
  photos: Recipe['photos']
  cover: string | null
  pick?: (photoId: string) => void
  busy: boolean
}) {
  return (
    <section aria-labelledby="dish-photos" className="flex flex-col gap-3">
      <h2 id="dish-photos" className="text-lg font-semibold">
        The dish
      </h2>
      <ul className={cn('grid gap-1.5', photos.length === 1 ? 'grid-cols-1' : 'grid-cols-2 sm:grid-cols-3')}>
        {photos.map((p, i) => (
          <li key={p.id} className={cn('relative', photos.length > 2 && i === 0 && 'col-span-2 row-span-2')}>
            <OriginalPhoto
              src={photoUrl(p.id, 'original')}
              fallback={photoUrl(p.ref, 'display')}
              alt={`${title}, photo of the dish ${i + 1} of ${photos.length}`}
            >
              <button
                type="button"
                className="block size-full overflow-hidden rounded-xl bg-muted outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                <img
                  src={photoUrl(p.ref, photos.length === 1 || i === 0 ? 'display' : 'thumb')}
                  width={p.width}
                  height={p.height}
                  alt={`${title}, photo of the dish ${i + 1} of ${photos.length}`}
                  loading="lazy"
                  decoding="async"
                  className={cn('size-full object-cover', photos.length === 1 ? 'aspect-[4/3]' : 'aspect-square')}
                />
              </button>
            </OriginalPhoto>
            {p.ref === cover ? (
              <TileMark />
            ) : (
              pick && (
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  disabled={busy}
                  onClick={() => pick(p.id)}
                  aria-label={`Use photo of the dish ${i + 1} on the recipe’s tile`}
                  title="Use on the recipe’s tile"
                  className="absolute top-1.5 right-1.5 size-8 rounded-full bg-background/90 shadow-paper"
                >
                  <Star aria-hidden />
                </Button>
              )
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

// Which photo is on the recipe's tile, and for its keeper a way to put this one there. Going back to the default
// stays in the edit form, beside the other photo choices.
function TileChoice({ onTile, busy, pick }: { onTile: boolean; busy: boolean; pick: () => void }) {
  if (onTile)
    return (
      <span className="flex min-h-9 items-center gap-1.5 text-sm font-medium">
        <Star className="size-3.5 fill-current" aria-hidden /> On the recipe’s tile
      </span>
    )
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      disabled={busy}
      onClick={pick}
      className="pointer-coarse:min-h-11"
    >
      <Star aria-hidden /> Use on the recipe’s tile
    </Button>
  )
}

// A small star on a photo that's on the recipe's tile.
function TileMark() {
  return (
    <span
      title="On the recipe’s tile"
      className="absolute top-1.5 right-1.5 grid size-6 place-items-center rounded-full bg-background/90 shadow-paper"
    >
      <Star className="size-3 fill-current" aria-hidden />
      <span className="sr-only">On the recipe’s tile</span>
    </span>
  )
}

// A section that folds open and shut, so the card and the ingredients come first.
function Fold({ title, open, children }: { title: string; open?: boolean; children: ReactNode }) {
  return (
    <details open={open} className="group border-b">
      <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between text-lg font-semibold outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 [&::-webkit-details-marker]:hidden">
        {title}
        <ChevronDown className="size-5 transition-transform group-open:rotate-180" aria-hidden />
      </summary>
      <div className="pt-1 pb-6">{children}</div>
    </details>
  )
}
